import { NavLink } from 'react-router-dom'
import { primaryNavFor } from '../lib/navigation'
import type { RailChoice } from '../lib/preferences'
import { Icon } from './Icon'

/**
 * The five destinations an operator works in all day.
 *
 * One markup tree at every width, with CSS deciding whether it is a left rail or a 56px tab bar along
 * the bottom. That is the design the masthead already had and the reason it survived two phases of new
 * sections: the component does not know how wide the window is, so it cannot be wrong about it, and
 * there is no second copy of the list to drift.
 *
 * Every destination is a real `<a href>` and all five are always present. `layout.spec.ts` reads them
 * without opening anything, which is what stops "no sideways scroll" being achieved by hiding half
 * the product - the defect that gate was written for, twice.
 *
 * The label is rendered at every width and hidden by CSS where it is not wanted rather than omitted,
 * so the accessible name comes from the same string an operator reads on a phone. A `title` alone
 * would leave the collapsed rail's buttons named by their icon, which is to say unnamed. There are
 * three presentations of that one string now - under the icon on the tab bar, under the icon on the
 * labeled rail, and hidden on the collapsed one - and it is still one string.
 */
export function PrimaryNav({
  roles,
  rail,
  onToggleRail
}: {
  roles: readonly string[]
  rail: RailChoice
  onToggleRail: () => void
}) {
  const groups = primaryNavFor(roles)

  return (
    <nav aria-label="Sections" className="primary-nav">
      {groups.map((group) => {
        const destination = group.items[0]
        if (!destination) return null

        return (
          <NavLink
            key={group.label}
            to={destination.to}
            className="primary-nav-item"
            title={group.label}
            data-testid={`nav-${group.label.toLowerCase()}`}
          >
            {group.icon && <Icon name={group.icon} size={20} />}
            <span className="primary-nav-label">{destination.label}</span>
          </NavLink>
        )
      })}

      {/*
        A toggle button, not a disclosure: every link above stays in the DOM in both states, so
        `aria-expanded` would promise a region that does not exist. `aria-pressed` says what is
        actually true - the names are showing - and lets the accessible name stay put rather than
        changing under somebody who is reading it.

        Deliberately not `nav-toggle`. That test id belonged to the phone drawer this rail replaced,
        and reusing it would point the next reader at a control that opened something.

        A <button> is invisible to both e2e navigation gates, which select and count `a` elements.
        That is what lets a sixth control live in a nav whose whole point is that it holds five.
      */}
      <button
        type="button"
        className="primary-nav-toggle"
        aria-pressed={rail === 'labeled'}
        onClick={onToggleRail}
        title="Section names"
        data-testid="rail-toggle"
      >
        <Icon name="sidebar-simple" size={18} />
        <span className="primary-nav-label">Section names</span>
      </button>
    </nav>
  )
}
